import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, Mail, Send, Loader2, CheckCircle, Clock, Building2, User, Calendar, Package, ClipboardCheck, XCircle, History, AlertCircle } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';
import { PaymentTypeBadge } from '@/components/PaymentTypeBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const APPROVAL_DOT_BG = {
  approved: 'bg-green-100',
  rejected: 'bg-red-100',
};
const getApprovalDotBg = (action) => APPROVAL_DOT_BG[action] || 'bg-amber-100';

function ItemRow({ item, index }) {
  const amount = Number(item.line_total || 0).toFixed(2);
  const rate = Number(item.purchase_rate || 0).toFixed(2);
  const gst = Number(item.gst_percentage || 0).toFixed(0);
  
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-2 text-center text-gray-500">{index + 1}</td>
      <td className="px-3 py-2">
        <p className="font-medium text-sm">{item.product_name}</p>
        <p className="text-xs text-gray-400">{item.product_sku || item.product_id?.slice(0, 8)}</p>
      </td>
      <td className="px-3 py-2 text-center">{item.quantity}</td>
      <td className="px-3 py-2 text-right">₹{rate}</td>
      <td className="px-3 py-2 text-center">{gst}%</td>
      <td className="px-3 py-2 text-right font-medium">₹{amount}</td>
    </tr>
  );
}

function POViewDialog({ open, onOpenChange, po, userAccess, onRefresh }) {
  const [downloading, setDownloading] = useState(null);
  const [sending, setSending] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  
  // Approval workflow states
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitComment, setSubmitComment] = useState('');
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  useEffect(() => {
    if (!open || !po?.id) return;
    setLoadingHistory(true);
    const token = localStorage.getItem('token');
    axios.get(`${API}/purchase-orders/${po.id}/approval-history`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(response => {
      setApprovalHistory(response.data.approval_history || []);
    }).catch(() => {
      /* Approval history fetch failed — list stays empty. */
    }).finally(() => {
      setLoadingHistory(false);
    });
    // API/axios are module-level imports; state setters are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, po?.id]);
  
  if (!po) return null;
  
  const statusText = String(po.status).toUpperCase().replace('_', ' ');
  const totalAmount = Number(po.total_amount || 0).toFixed(2);
  const subtotal = Number(po.subtotal || 0).toFixed(2);
  const cgst = Number(po.cgst || 0).toFixed(2);
  const sgst = Number(po.sgst || 0).toFixed(2);
  const itemsList = po.items || [];
  
  const canPerformActions = userAccess?.is_admin || userAccess?.role === 'manager' || ['admin', 'manager'].includes(userAccess?.user_role);
  const isPOSent = po.status === 'sent' || po.status === 'approved' || po.status === 'partially_received' || po.status === 'received';
  
  // Approval workflow checks
  const canSubmitForApproval = (po.status === 'draft' || po.status === 'rejected');
  const isWaitingApproval = po.status === 'waiting_approval';
  const isApproved = po.status === 'approved';
  const isRejected = po.status === 'rejected';
  
  const statusColors = {
    draft: 'bg-gray-100 text-gray-800',
    waiting_approval: 'bg-amber-100 text-amber-800',
    sent: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    partially_received: 'bg-yellow-100 text-yellow-800',
    received: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  
  async function handleSubmitForApproval() {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/purchase-orders/${po.id}/submit`, {
        comment: submitComment || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PO submitted for approval successfully!');
      setShowSubmitModal(false);
      setSubmitComment('');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit PO for approval');
    } finally {
      setSubmitting(false);
    }
  }
  
  async function handleDownloadPDF(copyType = 'internal') {
    setDownloading(copyType === 'supplier' ? 'supplier-pdf' : 'pdf');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/${po.id}/pdf?copy_type=${copyType}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const suffix = copyType === 'supplier' ? '_Supplier_Copy' : '_Internal';
      link.setAttribute('download', `${po.po_number}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${copyType === 'supplier' ? 'Supplier' : 'Internal'} PDF downloaded successfully`);
    } catch (err) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  }
  
  async function handleDownloadExcel() {
    setDownloading('excel');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/${po.id}/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${po.po_number}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded successfully');
    } catch (err) {
      toast.error('Failed to download Excel');
    } finally {
      setDownloading(null);
    }
  }
  
  async function handleSendPO() {
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/purchase-orders/${po.id}/send`, {
        recipient_email: sendEmail || null,
        message: sendMessage || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || 'PO sent successfully');
      setShowSendModal(false);
      setSendEmail('');
      setSendMessage('');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send PO');
    } finally {
      setSending(false);
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600" />
              <span>{po.po_number}</span>
              <Badge className={statusColors[po.status] || 'bg-gray-100 text-gray-800'}>
                {statusText}
              </Badge>
              {po.sent_at && (
                <Badge variant="outline" className="gap-1 text-xs">
                  <CheckCircle className="w-3 h-3" />
                  Sent {new Date(po.sent_at).toLocaleDateString()}
                </Badge>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Action Buttons */}
        {canPerformActions && (
          <div className="flex flex-wrap gap-2 py-3 border-b">
            {/* PDF Download Options */}
            <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-gray-50">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadPDF('supplier')}
                disabled={downloading === 'supplier-pdf'}
                className="gap-1 text-green-700 hover:text-green-800 hover:bg-green-50"
                title="Download Supplier Copy (No Pricing)"
              >
                {downloading === 'supplier-pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Supplier PDF
              </Button>
              <div className="w-px h-5 bg-gray-300"></div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadPDF('internal')}
                disabled={downloading === 'pdf'}
                className="gap-1 text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                title="Download Internal Copy (With Pricing)"
              >
                {downloading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Internal PDF
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadExcel}
              disabled={downloading === 'excel'}
              className="gap-2"
            >
              {downloading === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Export Excel
            </Button>
            <CsvDownloadButton onDownload={handleDownloadExcel} testId="po-view-export-csv" />
            
            {/* Submit for Approval Button */}
            {canSubmitForApproval && (
              <Button
                size="sm"
                onClick={() => setShowSubmitModal(true)}
                className="gap-2 bg-amber-600 hover:bg-amber-700"
                data-testid="submit-for-approval-btn"
              >
                <ClipboardCheck className="w-4 h-4" />
                Submit for Approval
              </Button>
            )}
            
            {/* Show waiting approval status */}
            {isWaitingApproval && (
              <Badge className="bg-amber-100 text-amber-800 gap-1">
                <Clock className="w-3 h-3" />
                Waiting for Approval
              </Badge>
            )}
            
            {!isPOSent && !canSubmitForApproval && !isWaitingApproval && (
              <Button
                size="sm"
                onClick={() => setShowSendModal(true)}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4" />
                Send PO
              </Button>
            )}
            {isPOSent && po.status === 'sent' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSendModal(true)}
                className="gap-2"
              >
                <Mail className="w-4 h-4" />
                Resend PO
              </Button>
            )}
          </div>
        )}
        
        {/* PO Details */}
        <div className="space-y-4 mt-4">
          {/* Info Grid - Only shows: Supplier, PO Date, Expected Delivery, Status */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs text-gray-500 uppercase">PO Date</p>
                <p className="font-medium">{po.po_date}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs text-gray-500 uppercase">Expected Delivery</p>
                <p className="font-medium">{po.expected_delivery_date || 'Not set'}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div>
                <p className="text-xs text-gray-500 uppercase">Payment Type</p>
                <PaymentTypeBadge type={po.payment_type} testId="po-view-payment-type" />
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs text-gray-500 uppercase">Supplier</p>
                <p className="font-medium">{po.supplier_name}</p>
                {po.supplier_gstin && <p className="text-xs text-gray-400">GSTIN: {po.supplier_gstin}</p>}
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Package className="w-4 h-4 text-gray-500 mt-1" />
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <Badge className={statusColors[po.status] || 'bg-gray-100 text-gray-800'}>
                  {statusText}
                </Badge>
              </div>
            </div>
          </div>
          
          {/* Items Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-600" />
              <span className="font-semibold text-sm">Order Items ({itemsList.length})</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-center w-12">#</th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-center w-20">Qty</th>
                  <th className="px-3 py-2 text-right w-24">Rate</th>
                  <th className="px-3 py-2 text-center w-16">GST</th>
                  <th className="px-3 py-2 text-right w-28">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {itemsList.map((item, idx) => (
                  <ItemRow key={item.product_id || idx} item={item} index={idx} />
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span>₹{subtotal}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">CGST:</span>
                <span>₹{cgst}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">SGST:</span>
                <span>₹{sgst}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-bold text-lg">
                <span>Grand Total:</span>
                <span className="text-green-600">₹{totalAmount}</span>
              </div>
            </div>
          </div>
          
          {/* Notes */}
          {po.notes && (
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-700 uppercase font-medium mb-1">Notes</p>
              <p className="text-sm">{po.notes}</p>
            </div>
          )}
          
          {/* Send Info */}
          {po.sent_at && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
              <p className="text-blue-800">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Sent on {new Date(po.sent_at).toLocaleString()} by {po.sent_by_name || 'Unknown'}
              </p>
            </div>
          )}
          
          {/* Rejection Reason */}
          {isRejected && po.rejection_reason && (
            <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-sm">
              <p className="text-red-800 font-medium flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Rejection Reason:
              </p>
              <p className="text-red-700 mt-1">{po.rejection_reason}</p>
              {po.rejected_by_name && (
                <p className="text-xs text-red-500 mt-2">
                  Rejected by {po.rejected_by_name} on {new Date(po.rejected_at).toLocaleString()}
                </p>
              )}
            </div>
          )}
          
          {/* Approval Info */}
          {isApproved && po.approved_at && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm">
              <p className="text-green-800">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Approved on {new Date(po.approved_at).toLocaleString()} by {po.approved_by_name || 'Unknown'}
              </p>
            </div>
          )}
          
          {/* Approval History */}
          {approvalHistory.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                <History className="w-4 h-4 text-gray-600" />
                <span className="font-semibold text-sm">Approval History</span>
              </div>
              <div className="p-3 space-y-3 max-h-48 overflow-y-auto">
                {approvalHistory.map((entry, idx) => (
                  <div key={`${entry.action}-${entry.timestamp}-${idx}`} className="flex items-start gap-3 text-sm">
                    <div className={`p-1 rounded-full ${getApprovalDotBg(entry.action)}`}>
                      {entry.action === 'approved' && <CheckCircle className="w-4 h-4 text-green-600" />}
                      {entry.action === 'rejected' && <XCircle className="w-4 h-4 text-red-600" />}
                      {entry.action === 'submitted' && <ClipboardCheck className="w-4 h-4 text-amber-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium capitalize">{entry.action}</p>
                      <p className="text-gray-500 text-xs">
                        By {entry.by_name} ({entry.by_role}) on {new Date(entry.timestamp).toLocaleString()}
                      </p>
                      {entry.comment && (
                        <p className="text-gray-600 mt-1 text-xs italic">"{entry.comment}"</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Send PO Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Send Purchase Order
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label>Recipient Email (Optional)</Label>
                  <Input
                    type="email"
                    value={sendEmail}
                    onChange={(e) => setSendEmail(e.target.value)}
                    placeholder="supplier@example.com"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to use supplier's registered email
                  </p>
                </div>
                
                <div>
                  <Label>Message (Optional)</Label>
                  <textarea
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    placeholder="Add a message for the supplier..."
                    className="mt-1 w-full p-2 border rounded-md text-sm"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowSendModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendPO} disabled={sending} className="gap-2">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? 'Sending...' : 'Send PO'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Submit for Approval Modal */}
        {showSubmitModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-amber-600" />
                Submit PO for Approval
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                This will submit <strong>{po.po_number}</strong> for approval by a Purchase Manager.
                {isRejected && (
                  <span className="block mt-2 text-amber-600">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    This PO was previously rejected. Make sure you've addressed the feedback before resubmitting.
                  </span>
                )}
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label>Comment (Optional)</Label>
                  <Textarea
                    value={submitComment}
                    onChange={(e) => setSubmitComment(e.target.value)}
                    placeholder="Add a note for the approver..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowSubmitModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmitForApproval} 
                  disabled={submitting} 
                  className="gap-2 bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default POViewDialog;
